/**
 * Owns the token lifecycle and the client that depends on it.
 *
 * Kept out of the App component so the sign-in state machine is testable and
 * the component stays a composition of screens.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { SimklClient } from "@/api/client";
import { pollForToken, requestPin } from "@/auth/pin";
import type { PinRequest } from "@/auth/pin";
import { APP_NAME, APP_VERSION, CLIENT_ID } from "@/config";
import { indexedDbVault, TokenHolder } from "@/store/vault";

export type Phase = "starting" | "signed-out" | "pairing" | "ready";

export interface Session {
  phase: Phase;
  pin: PinRequest | null;
  error: string | null;
  client: SimklClient;
  signIn: () => void;
  signOut: () => void;
  /** The Simkl token, for the one exchange that needs it: notification setup. */
  getToken: () => string | null;
}

function message(cause: unknown, fallback: string): string {
  return cause instanceof Error ? cause.message : fallback;
}

export function useSession(): Session {
  const holder = useMemo(() => new TokenHolder(indexedDbVault), []);
  const [phase, setPhase] = useState<Phase>("starting");
  const [pin, setPin] = useState<PinRequest | null>(null);
  const [error, setError] = useState<string | null>(null);

  const client = useMemo(
    () =>
      new SimklClient({
        clientId: CLIENT_ID,
        appName: APP_NAME,
        appVersion: APP_VERSION,
        getToken: () => holder.get(),
        // A 401 means the token died; there is no refresh to attempt.
        onAuthExpired: () => {
          setPhase("signed-out");
        },
      }),
    [holder],
  );

  useEffect(() => {
    void holder.load().then((token) => {
      setPhase(token === null ? "signed-out" : "ready");
    });
  }, [holder]);

  const signIn = useCallback(() => {
    setPhase("pairing");
    setError(null);
    void (async () => {
      try {
        const request = await requestPin(client);
        setPin(request);
        await holder.set(await pollForToken(client, request));
        setPin(null);
        setPhase("ready");
      } catch (cause) {
        setPin(null);
        setPhase("signed-out");
        setError(message(cause, "Sign-in failed"));
      }
    })();
  }, [client, holder]);

  const signOut = useCallback(() => {
    void holder.clear().then(() => {
      setPhase("signed-out");
    });
  }, [holder]);

  // Exposed only so notification setup can present it to the Worker once, in
  // exchange for a device key. Nothing else in the app reads it directly; the
  // holder has already loaded it by the time the session is ready.
  const getToken = useCallback(() => holder.get(), [holder]);

  return { phase, pin, error, client, signIn, signOut, getToken };
}
